import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Animated, Easing, TextInput, Image, Modal, Dimensions, ScrollView, ActivityIndicator } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import NavigationHeader from '../components/NavigationHeader';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient } from '../components/Gradients';
import AutocompleteDropdown from '../components/AutocompleteDropdown';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import LegBetCard from '../components/LegBetCard';
import { useAuth } from '../context/AuthContext';
import GradientButton from '../components/GradientButton';
import { Ionicons } from '@expo/vector-icons';
import { sportsData, getTeams, getLeagues, getMarkets, getApiLeague, getSportCode, SPORTSBOOKS, MARKET_CATEGORIES, TIME_FILTERS } from '../data/sportsData';
import { db } from '../firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';

const { width: screenWidth } = Dimensions.get('window');
const isMobile = screenWidth < 768;
const isTablet = screenWidth >= 768 && screenWidth < 1024;

// Comprehensive sportsbooks list - matches API sportsbooks
const ALL_SPORTSBOOKS = [
    // Tier 1: Most Popular US Legal
    { name: 'DraftKings', tier: 1, popular: true },
    { name: 'FanDuel', tier: 1, popular: true },
    { name: 'BetMGM', tier: 1, popular: true },
    { name: 'Caesars', tier: 1, popular: true },
    { name: 'ESPNBet', tier: 1, popular: true },
    { name: 'Fanatics', tier: 1, popular: true },
    { name: 'Bet365', tier: 1, popular: true },
    // Tier 2: Well-Known US Legal
    { name: 'BetRivers', tier: 2 },
    { name: 'PointsBet', tier: 2 },
    { name: 'Hard Rock', tier: 2 },
    { name: 'WynnBET', tier: 2 },
    { name: 'Unibet', tier: 2 },
    { name: 'Betfred', tier: 2 },
    { name: 'SuperBook', tier: 2 },
    { name: 'Tipico', tier: 2 },
    { name: 'BetParx', tier: 2 },
    { name: 'Borgata', tier: 2 },
    { name: 'Golden Nugget', tier: 2 },
    { name: 'Bally Bet', tier: 2 },
    { name: 'Circa Sports', tier: 2 },
    { name: 'Station Casinos', tier: 2 },
    { name: 'Betway', tier: 2 },
    { name: 'SI Sportsbook', tier: 2 },
    // Tier 3: Offshore / Sharp / International
    { name: 'Bovada', tier: 3 },
    { name: 'BetOnline', tier: 3 },
    { name: 'MyBookie', tier: 3 },
    { name: 'BetUS', tier: 3 },
    { name: 'Pinnacle', tier: 3 },
    { name: 'Bookmaker', tier: 3 },
    { name: 'Heritage Sports', tier: 3 },
    { name: 'BetAnySports', tier: 3 },
    { name: 'Jazz Sports', tier: 3 },
    { name: 'Sportsbetting.ag', tier: 3 },
    { name: 'GTBets', tier: 3 },
    { name: 'Everygame', tier: 3 },
    { name: 'Xbet', tier: 3 },
    { name: 'BetNow', tier: 3 },
    { name: 'Nitrogen', tier: 3 },
    { name: '5Dimes', tier: 3 },
    { name: 'Intertops', tier: 3 },
    // Tier 4: DFS / Props / Alternative
    { name: 'PrizePicks', tier: 4 },
    { name: 'Underdog', tier: 4 },
    { name: 'Sleeper', tier: 4 },
    { name: 'Fliff', tier: 4 },
    { name: 'Betr', tier: 4 },
    { name: 'Novig', tier: 4 },
    { name: 'ProphetX', tier: 4 },
    { name: 'Thrive Fantasy', tier: 4 },
    { name: 'Monkey Knife Fight', tier: 4 },
    { name: 'Dabble', tier: 4 },
];

// Boost percentage options
const BOOST_OPTIONS = [
    { value: '0', label: 'No Boost' },
    { value: '5', label: '5%' },
    { value: '10', label: '10%' },
    { value: '15', label: '15%' },
    { value: '20', label: '20%' },
    { value: '25', label: '25%' },
    { value: '30', label: '30%' },
    { value: '35', label: '35%' },
    { value: '40', label: '40%' },
    { value: '45', label: '45%' },
    { value: '50', label: '50%' },
    { value: '75', label: '75%' },
    { value: '100', label: '100%' },
];

// Get functions base URL
const getFunctionsBaseUrl = () => {
    if (Platform.OS === 'web') {
        const isLocalDev = window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1' ||
                          window.location.port === '19006';
        return isLocalDev 
            ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
            : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
    }
    return 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
};

const SameGameParlayScreen = () => {
    const { navigateTo } = useNavigation();
    const { userProfile } = useAuth();
    const { isTickerEnabled, totalHeaderHeight } = useTicker();
    const { addBackgroundQuery, canAddBackgroundQuery } = useBackgroundQuery();
    
    // Check if background query mode is enabled
    const backgroundQueryMode = userProfile?.preferences?.backgroundQueryMode === true;
    
    // Core state
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    
    // Bet Type state
    const [betType, setBetType] = useState('Straight');
    
    // Filter state
    const [selectedSport, setSelectedSport] = useState('');
    const [selectedLeague, setSelectedLeague] = useState('');
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedPlayer, setSelectedPlayer] = useState('');
    const [selectedMarket, setSelectedMarket] = useState('');
    const [selectedMarketCategory, setSelectedMarketCategory] = useState('');
    const [selectedSportsbooks, setSelectedSportsbooks] = useState([]);
    const [selectedTimeFilter, setSelectedTimeFilter] = useState(null);
    const [lineValue, setLineValue] = useState('');
    const [lineDirection, setLineDirection] = useState('');
    const [boostPercent, setBoostPercent] = useState('0'); // Profit boost percentage
    const [showSportsbooksDropdown, setShowSportsbooksDropdown] = useState(false);
    
    // Cached data for dropdowns (fetched from backend)
    const [cachedLeagues, setCachedLeagues] = useState([]);
    const [cachedTeams, setCachedTeams] = useState([]);
    const [cachedFixtures, setCachedFixtures] = useState([]);
    const [cachedMarkets, setCachedMarkets] = useState([]);
    const [loadingCacheData, setLoadingCacheData] = useState(false);
    
    // Fixtures state (for SGP mode)
    const [selectedFixture, setSelectedFixture] = useState(null);
    
    // Multi-query support
    const [queries, setQueries] = useState([]); // Array of query objects
    
    // SGP Legs state
    const [legs, setLegs] = useState([]);
    
    // Coming soon state
    const { hasComingSoonAccess } = useNavigation();
    const [showComingSoon, setShowComingSoon] = useState(false);
    const [sgpComingSoonEnabled, setSgpComingSoonEnabled] = useState(false);
    
    // Animation values
    const pageOpacity = useRef(new Animated.Value(0)).current;
    const pageTranslateY = useRef(new Animated.Value(20)).current;
    
    // Load SGP Coming Soon setting from Firestore
    useEffect(() => {
        const ref = doc(db, 'site_maintenance', 'global');
        const unsub = onSnapshot(ref, (snap) => {
            const d = snap.data() || {};
            const enabled = !!d.sgpComingSoonEnabled;
            setSgpComingSoonEnabled(enabled);
            setShowComingSoon(enabled && !hasComingSoonAccess);
        });
        return () => unsub();
    }, [hasComingSoonAccess]);
    
    // Page load animation
    useEffect(() => {
        Animated.parallel([
            Animated.timing(pageOpacity, { toValue: 1, duration: 800, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
            Animated.timing(pageTranslateY, { toValue: 0, duration: 800, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
        ]).start();
    }, []);

    // Fetch available data from cache on mount
    useEffect(() => {
        fetchCachedData();
    }, []);

    // Fetch cached leagues/teams/fixtures/markets from backend
    const fetchCachedData = async () => {
        setLoadingCacheData(true);
        try {
            const base = getFunctionsBaseUrl();
            const response = await fetch(`${base}/getAllCachedFixtures?limit=200`);
            const data = await response.json();
            
            if (data.fixtures && Array.isArray(data.fixtures)) {
                // Extract unique leagues
                const leagues = [...new Set(data.fixtures.map(f => f.league).filter(Boolean))];
                setCachedLeagues(leagues.map(l => l.toUpperCase()));
    
                // Extract unique teams
                const teams = new Set();
                data.fixtures.forEach(f => {
                    if (f.home_team) teams.add(f.home_team);
                    if (f.away_team) teams.add(f.away_team);
                });
                setCachedTeams([...teams].sort());
    
                // Store fixtures
                setCachedFixtures(data.fixtures.map(f => ({
                    id: f.id,
                    label: `${f.away_team} @ ${f.home_team}`,
                    home_team: f.home_team,
                    away_team: f.away_team,
                    start_date: f.start_date,
                    league: f.league,
                    total_markets: f.total_markets
                })));
                
                // Extract unique markets if available
                const markets = new Set();
                data.fixtures.forEach(f => {
                    if (f.markets && typeof f.markets === 'object') {
                        Object.keys(f.markets).forEach(m => markets.add(m));
                    }
                });
                if (markets.size > 0) {
                    setCachedMarkets([...markets].sort());
                }
                
                console.log('[Optimizer] Loaded cache data:', {
                    leagues: leagues.length,
                    teams: teams.size,
                    fixtures: data.fixtures.length,
                    markets: markets.size
                });
            }
        } catch (error) {
            console.error('[Optimizer] Error fetching cache data:', error);
        } finally {
            setLoadingCacheData(false);
            }
    };

    // Reset dependent fields when sport changes
    useEffect(() => {
        setSelectedLeague('');
        setSelectedTeam('');
        setSelectedMarket('');
        setSelectedFixture(null);
    }, [selectedSport]);

    // Reset team when league changes
    useEffect(() => {
        setSelectedTeam('');
        setSelectedFixture(null);
    }, [selectedLeague]);

    // Get filtered options - combine static data with cached data
    const getLeagueOptions = () => {
        const staticLeagues = getLeagues(selectedSport);
        // Combine with cached leagues, removing duplicates
        const combined = [...new Set([...staticLeagues, ...cachedLeagues])];
        return combined.length > 0 ? combined : staticLeagues;
    };
    
    const getTeamOptions = () => {
        if (selectedFixture) {
            return [selectedFixture.home_team, selectedFixture.away_team].filter(Boolean);
        }
        
        // Filter cached teams by selected league if applicable
        if (selectedLeague && cachedFixtures.length > 0) {
            const teamsInLeague = new Set();
            cachedFixtures
                .filter(f => f.league?.toLowerCase() === selectedLeague.toLowerCase())
                .forEach(f => {
                    if (f.home_team) teamsInLeague.add(f.home_team);
                    if (f.away_team) teamsInLeague.add(f.away_team);
                });
            if (teamsInLeague.size > 0) {
                return [...teamsInLeague].sort();
            }
        }
        
        // Fall back to static data
        const staticTeams = getTeams(selectedSport, selectedLeague);
        return staticTeams.length > 0 ? staticTeams : cachedTeams;
    };
    
    const getMarketOptions = () => {
        if (selectedMarketCategory && MARKET_CATEGORIES[selectedMarketCategory]) {
            return MARKET_CATEGORIES[selectedMarketCategory];
        }
        const staticMarkets = getMarkets(selectedSport, 'all');
        // Combine with cached markets
        const combined = [...new Set([...staticMarkets, ...cachedMarkets])];
        return combined.length > 0 ? combined : staticMarkets;
    };
    
    const getFixtureOptions = () => {
        // Filter by league if selected
        let fixtures = cachedFixtures;
        if (selectedLeague) {
            fixtures = cachedFixtures.filter(f => 
                f.league?.toLowerCase() === selectedLeague.toLowerCase()
            );
        }
        return fixtures.map(f => f.label);
    };

    // Handle fixture selection
    const handleFixtureSelect = (fixtureLabel) => {
        const fixture = cachedFixtures.find(f => f.label === fixtureLabel);
        setSelectedFixture(fixture);
                setSelectedTeam('');
    };

    // Build query from current filters
    const buildQueryFromFilters = () => {
        const parts = [];
        
        if (selectedTeam) parts.push(selectedTeam);
        if (selectedPlayer) parts.push(selectedPlayer);
        if (selectedMarket) parts.push(selectedMarket);
        if (lineValue && lineDirection) {
            parts.push(`${lineDirection} ${lineValue}`);
        } else if (lineValue) {
            parts.push(lineValue);
        }
        if (selectedTimeFilter) {
            const timeLabel = TIME_FILTERS.find(t => t.value === selectedTimeFilter)?.label;
            if (timeLabel && timeLabel !== 'All Games') parts.push(timeLabel.toLowerCase());
        }
        
        return parts.join(' ') || (selectedLeague || selectedSport || 'odds');
    };

    // Add current selection as a query
    const addQuery = () => {
        const queryText = buildQueryFromFilters();
        if (!queryText || queryText === 'odds') {
            setErrorMsg('Please select at least one filter before adding a query');
            return;
        }
        
        const newQuery = {
            id: Date.now(),
            text: queryText,
            sport: selectedSport,
            league: selectedLeague,
            team: selectedTeam,
            player: selectedPlayer,
            market: selectedMarket,
            line: lineValue,
            direction: lineDirection
        };
        
        setQueries(prev => [...prev, newQuery]);
        
        // Clear current filters for next query
        setSelectedTeam('');
        setSelectedPlayer('');
        setSelectedMarket('');
        setLineValue('');
        setLineDirection('');
        setErrorMsg('');
    };

    // Remove a query
    const removeQuery = (queryId) => {
        setQueries(prev => prev.filter(q => q.id !== queryId));
    };

    // Add leg for SGP mode
    const addLeg = () => {
        if (!selectedMarket && !selectedTeam && !selectedPlayer) {
            setErrorMsg('Please select at least a market, team, or player');
            return;
        }
        
        const legDescription = buildQueryFromFilters();
        const newLeg = {
            id: Date.now(),
            sport: selectedSport,
            league: selectedLeague,
            fixture: selectedFixture?.label,
            team: selectedTeam,
            player: selectedPlayer,
            market: selectedMarket,
            line: lineValue,
            direction: lineDirection,
            description: legDescription
        };
        
        setLegs(prev => [...prev, newLeg]);
        
        // Clear current selections for next leg
        setSelectedTeam('');
        setSelectedPlayer('');
        setSelectedMarket('');
        setLineValue('');
        setLineDirection('');
        setErrorMsg('');
    };
    
    // Remove a leg
    const removeLeg = (legId) => {
        setLegs(prev => prev.filter(leg => leg.id !== legId));
    };
    
    // Clear all filters
    const clearAllFilters = () => {
        setSelectedSport('');
        setSelectedLeague('');
        setSelectedTeam('');
        setSelectedPlayer('');
        setSelectedMarket('');
        setSelectedMarketCategory('');
        setSelectedSportsbooks([]);
        setSelectedTimeFilter(null);
        setLineValue('');
        setLineDirection('');
        setSelectedFixture(null);
        setLegs([]);
        setQueries([]);
        setErrorMsg('');
    };

    // Build final query string
    const getFinalQuery = () => {
        if (betType === 'SGP') {
            if (legs.length > 0) {
                return legs.map(l => l.description).join(' + ') + ' sgp';
            }
            return '';
        } else {
            // For Straight, combine all added queries with AND, plus current filters
            const currentQuery = buildQueryFromFilters();
            const allQueries = [...queries.map(q => q.text)];
            if (currentQuery && currentQuery !== 'odds') {
                allQueries.push(currentQuery);
            }
            return allQueries.join(' AND ') || '';
        }
    };

    // Run the search
    const runSearch = async (forceBackground = false) => {
        const query = getFinalQuery();
        
        if (!query) {
        if (betType === 'SGP') {
                setErrorMsg('Please add at least 2 legs for a Same Game Parlay');
            } else {
                setErrorMsg('Please select at least one filter or add a query');
            }
            return;
        }
        
        if (betType === 'SGP' && legs.length < 2) {
            setErrorMsg('Please add at least 2 legs for a Same Game Parlay');
            return;
        }
        
        console.log('[Optimizer] Running search:', query, 'background:', forceBackground || backgroundQueryMode);
        setErrorMsg('');
        
        // Check if background query mode is enabled
        const useBackground = forceBackground || (backgroundQueryMode && canAddBackgroundQuery);
        
        if (useBackground) {
            // Add to background queue - no page navigation
            await addBackgroundQuery(query, {
                sport: getSportCode(selectedSport),
                league: getApiLeague(selectedSport, selectedLeague),
                team: selectedTeam,
                player: selectedPlayer,
                market: selectedMarket,
                sportsbooks: selectedSportsbooks,
                fromOptimizer: true,
            });
                return;
            }
            
        // Navigate to SgpFlow with the query (foreground mode)
        navigateTo('SgpFlow', { 
            payload: { 
                query, 
                fromOptimizer: true,
                filters: {
                    sport: getSportCode(selectedSport),
                    league: getApiLeague(selectedSport, selectedLeague),
                    team: selectedTeam,
                    player: selectedPlayer,
                    market: selectedMarket,
                    sportsbooks: selectedSportsbooks,
                    timeFilter: selectedTimeFilter
                }
            } 
        });
    };

    // Toggle sportsbook selection
    const toggleSportsbook = (book) => {
        setSelectedSportsbooks(prev => 
            prev.includes(book) 
                ? prev.filter(b => b !== book)
                : [...prev, book]
        );
    };

    const finalQuery = getFinalQuery();

    return (
        <View style={styles.container}>
            <View style={styles.background} />
            <View style={[styles.splashContainer, { 
                paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + (isMobile ? 12 : 32)) : (isMobile ? 12 : 16) 
            }]}>
                <NavigationHeader />
                
                {/* Coming Soon Modal */}
                {showComingSoon && (
                    <Modal visible={true} transparent={true} animationType="fade">
                        <View style={styles.comingSoonOverlay}>
                            <BlurView intensity={80} tint="dark" style={styles.comingSoonBlur}>
                                <View style={styles.comingSoonModal}>
                                    <LinearGradient
                                        colors={['rgba(162, 89, 255, 0.1)', 'rgba(162, 89, 255, 0.05)']}
                                        style={styles.comingSoonGradient}
                                    >
                                        <Image 
                                            source={require('../assets/icon.png')} 
                                            style={{ width: 64, height: 64, marginBottom: 24 }}
                                            resizeMode="contain"
                                        />
                                        <Text style={styles.comingSoonTitle}>Coming Soon</Text>
                                        <Text style={styles.comingSoonText}>
                                            The Bet Optimizer is currently in beta.{'\n\n'}
                                            Stay tuned for the official launch!
                                        </Text>
                                        <GradientButton
                                            text="Go Home"
                                            onPress={() => navigateTo('Home')}
                                            style={styles.comingSoonButton}
                                            gradientColors={["#A259FF", "#E0BBFF"]}
                                        />
                                    </LinearGradient>
                                </View>
                            </BlurView>
                        </View>
                    </Modal>
                )}

                <ScrollView 
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                <Animated.View style={[
                    styles.mainContent,
                        { opacity: pageOpacity, transform: [{ translateY: pageTranslateY }] }
                ]}>
                    <BlurView tint="dark" style={styles.mainContentBlur}>
                            {/* Header Section */}
                    <View style={styles.titleSection}>
                                <View style={styles.titleRow}>
                                    <View style={styles.titleTextContainer}>
                            <Text style={styles.title}>
                                            {betType === 'SGP' ? 'PARLAY' : 'ODDS'}{'\n'}OPTIMIZER
                            </Text>
                        <Text style={styles.subtitle}>
                            {betType === 'SGP' 
                                                ? 'Build a Same Game Parlay with granular filters'
                                                : 'Search for the best odds with precise filters'
                            }
                        </Text>
                    </View>
                                    <View style={styles.searchTypeContainer}>
                                        <AutocompleteDropdown
                                            label="Search Type"
                                            value={betType}
                                            placeholder="Select Type"
                                            options={['Straight', 'SGP']}
                                            onValueChange={(v) => {
                                                setBetType(v);
                                                setLegs([]);
                                                setQueries([]);
                                            }}
                                            zIndex={100}
                                        />
                                </View>
                            </View>

                                {/* Cache status indicator */}
                                {loadingCacheData ? (
                                    <View style={styles.cacheStatus}>
                                        <ActivityIndicator size="small" color="#8b5cf6" />
                                        <Text style={styles.cacheStatusText}>Loading data...</Text>
                                        </View>
                                ) : cachedFixtures.length > 0 && (
                                    <View style={styles.cacheStatus}>
                                        <Ionicons name="checkmark-circle" size={16} color="#22c55e" />
                                        <Text style={styles.cacheStatusText}>
                                            ✓ Loaded from cache
                                                            </Text>
                                                                </View>
                                                            )}
                                                        </View>

                            {/* Main Filters Section */}
                            <View style={[styles.filtersSection, { zIndex: 1000, position: 'relative' }]}>
                                <View style={[styles.filtersRow, { zIndex: 500, position: 'relative' }]}>
                                    {/* Left Column */}
                            <View style={[styles.filtersColumn, { zIndex: 500 }]}>
                                <AutocompleteDropdown
                                    label="Sport"
                                    value={selectedSport}
                                            placeholder="Select sport..."
                                    options={Object.keys(sportsData)}
                                    onValueChange={setSelectedSport}
                                            zIndex={300}
                                    />
                                <AutocompleteDropdown
                                    label="League"
                                    value={selectedLeague}
                                            placeholder={cachedLeagues.length > 0 ? `${cachedLeagues.length} available...` : "Select league..."}
                                    options={getLeagueOptions()}
                                    onValueChange={setSelectedLeague}
                                            zIndex={290}
                                />
                                
                                        {/* Fixture selector for SGP mode */}
                                {betType === 'SGP' && (
                                        <AutocompleteDropdown
                                                label="Game/Fixture"
                                                value={selectedFixture?.label || ''}
                                                placeholder={`${getFixtureOptions().length} games available...`}
                                                options={getFixtureOptions()}
                                                onValueChange={handleFixtureSelect}
                                                zIndex={280}
                                        />
                                )}
                                
                                <AutocompleteDropdown
                                    label="Team"
                                    value={selectedTeam}
                                            placeholder={`${getTeamOptions().length} teams available...`}
                                    options={getTeamOptions()}
                                    onValueChange={setSelectedTeam}
                                            zIndex={270}
                                />
                                        
                                        <View style={styles.inputGroup}>
                                            <Text style={styles.inputLabel}>Player (optional)</Text>
                                            <View style={styles.textInputWrap}>
                                        <TextInput
                                            placeholder="Type player name..."
                                            placeholderTextColor="rgba(255,255,255,0.4)"
                                            style={styles.textInputField}
                                            value={selectedPlayer}
                                            onChangeText={setSelectedPlayer}
                                        />
                                            </View>
                                </View>
                            </View>

                                    {/* Right Column */}
                            <View style={[styles.filtersColumn, { zIndex: 400 }]}>
                                <AutocompleteDropdown
                                            label="Market Category"
                                            value={selectedMarketCategory}
                                            placeholder="All categories..."
                                            options={Object.keys(MARKET_CATEGORIES)}
                                            onValueChange={(v) => {
                                                setSelectedMarketCategory(v);
                                                setSelectedMarket('');
                                            }}
                                            zIndex={260}
                                />
                                        
                                <AutocompleteDropdown
                                            label="Market"
                                            value={selectedMarket}
                                            placeholder={`${getMarketOptions().length} markets available...`}
                                            options={getMarketOptions()}
                                            onValueChange={setSelectedMarket}
                                            zIndex={250}
                                        />
                                        
                                        <View style={[styles.lineRow, { zIndex: 240 }]}>
                                            <View style={styles.lineValueContainer}>
                                                <Text style={styles.inputLabel}>Line Value</Text>
                                                <View style={styles.textInputWrap}>
                                                    <TextInput
                                                        placeholder="e.g., 25.5"
                                                        placeholderTextColor="rgba(255,255,255,0.4)"
                                                        style={styles.textInputField}
                                                        value={lineValue}
                                                        onChangeText={setLineValue}
                                                        keyboardType="numeric"
                                                    />
                                                </View>
                                            </View>
                                            <View style={styles.directionContainer}>
                                <AutocompleteDropdown
                                                    label="Direction"
                                                    value={lineDirection}
                                                    placeholder="O/U"
                                                    options={['Over', 'Under']}
                                                    onValueChange={setLineDirection}
                                                    zIndex={240}
                                />
                                            </View>
                                        </View>
                                        
                                <AutocompleteDropdown
                                            label="Time Filter"
                                            value={TIME_FILTERS.find(t => t.value === selectedTimeFilter)?.label || ''}
                                            placeholder="All games..."
                                            options={TIME_FILTERS.map(t => t.label)}
                                            onValueChange={(label) => {
                                                const filter = TIME_FILTERS.find(t => t.label === label);
                                                setSelectedTimeFilter(filter?.value || null);
                                            }}
                                            zIndex={230}
                                />
                            </View>
                        </View>

                                {/* Boost % and Sportsbooks Row */}
                                <View style={[styles.filtersRow, { marginTop: 8, zIndex: 100, position: 'relative' }]}>
                                    {/* Boost Percentage Dropdown */}
                                    <View style={{ flex: isMobile ? 1 : 0.4, minWidth: isMobile ? '100%' : 150, zIndex: 90 }}>
                                        <AutocompleteDropdown
                                            label="Profit Boost %"
                                            value={BOOST_OPTIONS.find(b => b.value === boostPercent)?.label || 'No Boost'}
                                            placeholder="No Boost"
                                            options={BOOST_OPTIONS.map(b => b.label)}
                                            onValueChange={(label) => {
                                                const opt = BOOST_OPTIONS.find(b => b.label === label);
                                                setBoostPercent(opt?.value || '0');
                                            }}
                                            zIndex={90}
                                        />
                                    </View>
                                    
                                    {/* Sportsbooks Multi-Select Dropdown */}
                                    <View style={{ flex: 1, position: 'relative', zIndex: showSportsbooksDropdown ? 99999 : 80 }}>
                                        <Text style={styles.inputLabel}>
                                            Sportsbooks ({selectedSportsbooks.length} selected)
                                        </Text>
                                        <TouchableOpacity
                                            style={styles.sportsbooksSelector}
                                            onPress={() => setShowSportsbooksDropdown(!showSportsbooksDropdown)}
                                        >
                                            <Text style={styles.sportsbooksSelectorText} numberOfLines={1}>
                                                {selectedSportsbooks.length === 0 
                                                    ? 'All Sportsbooks (click to filter)' 
                                                    : selectedSportsbooks.slice(0, 3).join(', ') + (selectedSportsbooks.length > 3 ? ` +${selectedSportsbooks.length - 3} more` : '')
                                                }
                                            </Text>
                                            <Text style={styles.sportsbooksArrow}>{showSportsbooksDropdown ? '▲' : '▼'}</Text>
                                        </TouchableOpacity>
                                        
                                        {showSportsbooksDropdown && (
                                            <View style={styles.sportsbooksDropdown}>
                                                <View style={styles.sportsbooksDropdownHeader}>
                                                    <TouchableOpacity 
                                                        onPress={() => setSelectedSportsbooks(ALL_SPORTSBOOKS.filter(b => b.popular).map(b => b.name))} 
                                                        style={styles.sportsbooksQuickAction}
                                                    >
                                                        <Text style={styles.sportsbooksQuickActionText}>Select Popular</Text>
                                                    </TouchableOpacity>
                                                    <TouchableOpacity 
                                                        onPress={() => setSelectedSportsbooks([])} 
                                                        style={[styles.sportsbooksQuickAction, { backgroundColor: 'rgba(248,113,113,0.2)' }]}
                                                    >
                                                        <Text style={[styles.sportsbooksQuickActionText, { color: '#f87171' }]}>Clear</Text>
                                                    </TouchableOpacity>
                                                    <TouchableOpacity onPress={() => setShowSportsbooksDropdown(false)} style={styles.sportsbooksCloseBtn}>
                                                        <Text style={styles.sportsbooksCloseBtnText}>Done ✓</Text>
                                                    </TouchableOpacity>
                                                </View>
                                                
                                                <ScrollView style={styles.sportsbooksList} nestedScrollEnabled>
                                                    {/* Popular */}
                                                    <Text style={styles.sportsbooksTierLabel}>⭐ Most Popular</Text>
                                                    <View style={styles.sportsbooksGridDropdown}>
                                                        {ALL_SPORTSBOOKS.filter(b => b.tier === 1).map((book) => (
                                                            <TouchableOpacity
                                                                key={book.name}
                                                                style={[
                                                                    styles.sportsbookChip,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                                ]}
                                                                onPress={() => toggleSportsbook(book.name)}
                                                            >
                                                                <Text style={[
                                                                    styles.sportsbookChipText,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                                ]}>
                                                                    {book.name}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        ))}
                                                    </View>
                                                    
                                                    {/* US Legal */}
                                                    <Text style={styles.sportsbooksTierLabel}>🇺🇸 US Legal</Text>
                                                    <View style={styles.sportsbooksGridDropdown}>
                                                        {ALL_SPORTSBOOKS.filter(b => b.tier === 2).map((book) => (
                                                            <TouchableOpacity
                                                                key={book.name}
                                                                style={[
                                                                    styles.sportsbookChip,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                                ]}
                                                                onPress={() => toggleSportsbook(book.name)}
                                                            >
                                                                <Text style={[
                                                                    styles.sportsbookChipText,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                                ]}>
                                                                    {book.name}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        ))}
                                                    </View>
                                                    
                                                    {/* Offshore/Sharp */}
                                                    <Text style={styles.sportsbooksTierLabel}>🌐 Offshore / Sharp</Text>
                                                    <View style={styles.sportsbooksGridDropdown}>
                                                        {ALL_SPORTSBOOKS.filter(b => b.tier === 3).map((book) => (
                                                            <TouchableOpacity
                                                                key={book.name}
                                                                style={[
                                                                    styles.sportsbookChip,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                                ]}
                                                                onPress={() => toggleSportsbook(book.name)}
                                                            >
                                                                <Text style={[
                                                                    styles.sportsbookChipText,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                                ]}>
                                                                    {book.name}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        ))}
                                                    </View>
                                                    
                                                    {/* DFS / Props */}
                                                    <Text style={styles.sportsbooksTierLabel}>🎲 DFS / Props</Text>
                                                    <View style={styles.sportsbooksGridDropdown}>
                                                        {ALL_SPORTSBOOKS.filter(b => b.tier === 4).map((book) => (
                                                            <TouchableOpacity
                                                                key={book.name}
                                                                style={[
                                                                    styles.sportsbookChip,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                                ]}
                                                                onPress={() => toggleSportsbook(book.name)}
                                                            >
                                                                <Text style={[
                                                                    styles.sportsbookChipText,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                                ]}>
                                                                    {book.name}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        ))}
                                                    </View>
                                                </ScrollView>
                                            </View>
                                        )}
                                    </View>
                                </View>
                                
                                {/* Selected Sportsbooks Summary */}
                                {selectedSportsbooks.length > 0 && (
                                    <View style={styles.selectedSportsbooksSummary}>
                                        <View style={styles.selectedSportsbooksHeader}>
                                            <Text style={styles.selectedSportsbooksLabel}>
                                                🎯 Filtering: {selectedSportsbooks.length} book{selectedSportsbooks.length !== 1 ? 's' : ''}
                                            </Text>
                                            <TouchableOpacity onPress={() => setSelectedSportsbooks([])}>
                                                <Text style={styles.clearSportsbooksBtn}>Clear</Text>
                                            </TouchableOpacity>
                                        </View>
                                        <View style={styles.selectedSportsbooksChips}>
                                            {selectedSportsbooks.map((book) => (
                                                <TouchableOpacity 
                                                    key={book} 
                                                    style={styles.selectedSportsbookChip}
                                                    onPress={() => toggleSportsbook(book)}
                                                >
                                                    <Text style={styles.selectedSportsbookChipText}>{book}</Text>
                                                    <Text style={styles.selectedSportsbookRemove}>×</Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                    </View>
                                )}

                                {/* Added Queries Display (Straight mode) */}
                                {betType === 'Straight' && queries.length > 0 && (
                                    <View style={styles.queriesSection}>
                                        <Text style={styles.sectionLabel}>Combined Queries ({queries.length})</Text>
                                        {queries.map((q, index) => (
                                            <View key={q.id} style={styles.queryCard}>
                                                <View style={styles.queryCardContent}>
                                                    <Text style={styles.queryCardNumber}>Query {index + 1}</Text>
                                                    <Text style={styles.queryCardText}>{q.text}</Text>
                                                </View>
                                                <TouchableOpacity 
                                                    style={styles.removeButton}
                                                    onPress={() => removeQuery(q.id)}
                                                >
                                                    <Text style={styles.removeButtonText}>×</Text>
                                                </TouchableOpacity>
                                            </View>
                                        ))}
                                    </View>
                                )}

                                {/* SGP Legs Display */}
                            {betType === 'SGP' && legs.length > 0 && (
                                    <View style={styles.legsSection}>
                                        <Text style={styles.sectionLabel}>Parlay Legs ({legs.length})</Text>
                                    {legs.map((leg, index) => (
                                        <View key={leg.id} style={styles.legCard}>
                                            <View style={styles.legCardContent}>
                                                <Text style={styles.legCardNumber}>Leg {index + 1}</Text>
                                                <Text style={styles.legCardDescription}>{leg.description}</Text>
                                                    {leg.fixture && (
                                                        <Text style={styles.legCardDetails}>{leg.fixture}</Text>
                                                    )}
                                            </View>
                                            <TouchableOpacity 
                                                    style={styles.removeButton}
                                                onPress={() => removeLeg(leg.id)}
                                            >
                                                    <Text style={styles.removeButtonText}>×</Text>
                                            </TouchableOpacity>
                                        </View>
                                    ))}
                                </View>
                            )}
                            
                                {/* Query Preview */}
                                <View style={styles.queryPreview}>
                                    <Text style={styles.queryPreviewLabel}>QUERY PREVIEW:</Text>
                                    <Text style={styles.queryPreviewText}>
                                        {finalQuery || '(Select filters to build your query)'}
                                </Text>
                                </View>

                                {/* Error Message */}
                                {errorMsg && (
                                    <View style={styles.errorContainer}>
                                        <Text style={styles.errorText}>{errorMsg}</Text>
                                    </View>
                            )}
                            
                                {/* Action Buttons */}
                            <View style={styles.actionButtons}>
                                {betType === 'SGP' ? (
                                        <TouchableOpacity 
                                            style={[styles.actionButton, styles.addButton]}
                                            onPress={addLeg}
                                        >
                                            <Ionicons name="add-circle-outline" size={18} color="#fff" />
                                            <Text style={styles.actionButtonText}>Add Leg</Text>
                                        </TouchableOpacity>
                                    ) : (
                                        <TouchableOpacity 
                                            style={[styles.actionButton, styles.addButton]}
                                            onPress={addQuery}
                                        >
                                            <Ionicons name="add-circle-outline" size={18} color="#fff" />
                                            <Text style={styles.actionButtonText}>Add Query</Text>
                                        </TouchableOpacity>
                                        )}
                                    
                                        <TouchableOpacity 
                                            style={[styles.actionButton, styles.clearButton]} 
                                        onPress={clearAllFilters}
                                        >
                                        <Ionicons name="refresh-outline" size={18} color="#fff" />
                                        <Text style={styles.actionButtonText}>Clear All</Text>
                                        </TouchableOpacity>
                                    
                                        <TouchableOpacity 
                                            style={[
                                                styles.actionButton, 
                                                styles.searchButton,
                                            (!finalQuery || loading) && styles.actionButtonDisabled
                                            ]} 
                                        onPress={runSearch}
                                        disabled={!finalQuery || loading}
                                    >
                                        <Ionicons name="search-outline" size={18} color="#fff" />
                                        <Text style={styles.actionButtonText}>
                                            {loading ? 'Searching...' : 'Search'}
                                    </Text>
                                    </TouchableOpacity>
                            </View>
                    </View>
                    </BlurView>
                </Animated.View>
                </ScrollView>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#131313',
    },
    background: {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: '#131313',
    },
    splashContainer: {
        flex: 1,
        width: '100%',
        height: '100%',
        paddingHorizontal: isMobile ? 12 : 48,
        paddingBottom: isMobile ? 16 : 48,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
        paddingBottom: 450, // Extra space for sportsbooks dropdown
    },
    mainContent: {
        width: '100%',
        maxWidth: 1200,
        alignSelf: 'center',
        borderRadius: isMobile ? 12 : 20,
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.25)',
        borderLeftColor: 'rgba(255,255,255,0.18)',
        borderRightColor: 'rgba(255,255,255,0.12)',
        borderBottomColor: 'rgba(255,255,255,0.08)',
        overflow: 'visible',
    },
    mainContentBlur: {
        padding: isMobile ? 16 : 32,
        gap: isMobile ? 20 : 28,
        borderRadius: isMobile ? 12 : 20,
        backgroundColor: 'rgba(19, 19, 19, 0.85)',
    },
    titleSection: {
        gap: 12,
    },
    titleRow: {
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        gap: 16,
    },
    titleTextContainer: {
        flex: 1,
        gap: 8,
    },
    title: {
        color: 'rgba(255, 255, 255, 0.85)',
        fontSize: isMobile ? 28 : 36,
        fontFamily: 'ClimateCrisis',
        fontWeight: '400',
        textTransform: 'uppercase',
        lineHeight: isMobile ? 32 : 40,
    },
    subtitle: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: isMobile ? 14 : 15,
        fontFamily: 'Slate',
    },
    searchTypeContainer: {
        width: isMobile ? '100%' : 180,
        zIndex: 100,
    },
    cacheStatus: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderRadius: 8,
        alignSelf: 'flex-start',
    },
    cacheStatusText: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 13,
    },
    filtersSection: {
        gap: isMobile ? 16 : 24,
        position: 'relative',
    },
    filtersRow: {
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 16 : 32,
        position: 'relative',
    },
    filtersColumn: {
        flex: 1,
        gap: isMobile ? 14 : 18,
        minWidth: 0, // Prevent overflow
        position: 'relative',
    },
    inputGroup: {
        gap: 8,
    },
    inputLabel: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 14,
        fontWeight: '600',
    },
    textInputWrap: {
        borderRadius: 8,
        paddingVertical: 14,
        paddingHorizontal: 16,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.3)',
        backgroundColor: '#1a1a1a',
    },
    textInputField: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 15,
        fontWeight: '500',
        ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
    },
    lineRow: {
        flexDirection: 'row',
        gap: 12,
        width: '100%',
    },
    lineValueContainer: {
        flex: 1,
        gap: 8,
    },
    directionContainer: {
        flex: 1,
        minWidth: 0,
    },
    sectionLabel: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 15,
        fontWeight: '700',
        marginBottom: 10,
    },
    sportsbooksSection: {
        marginTop: 4,
    },
    sportsbooksGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    sportsbookChip: {
        paddingVertical: 8,
        paddingHorizontal: 14,
        borderRadius: 20,
        backgroundColor: '#252525',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.25)',
    },
    sportsbookChipSelected: {
        backgroundColor: '#4c2889',
        borderColor: '#8b5cf6',
    },
    sportsbookChipText: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: 13,
        fontWeight: '500',
    },
    sportsbookChipTextSelected: {
        color: '#e9d5ff',
        fontWeight: '600',
    },
    queriesSection: {
        padding: 16,
        backgroundColor: 'rgba(59, 130, 246, 0.05)',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(59, 130, 246, 0.2)',
    },
    queryCard: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12,
        marginBottom: 8,
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(59, 130, 246, 0.3)',
    },
    queryCardContent: {
        flex: 1,
    },
    queryCardNumber: {
        fontSize: 11,
        fontWeight: '600',
        color: '#3b82f6',
        marginBottom: 4,
        textTransform: 'uppercase',
    },
    queryCardText: {
        fontSize: 14,
        fontWeight: '600',
        color: 'rgba(255,255,255,0.9)',
    },
    legsSection: {
        padding: 16,
        backgroundColor: 'rgba(139, 92, 246, 0.05)',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(139, 92, 246, 0.2)',
    },
    legCard: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12,
        marginBottom: 8,
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(139, 92, 246, 0.3)',
    },
    legCardContent: {
        flex: 1,
    },
    legCardNumber: {
        fontSize: 11,
        fontWeight: '600',
        color: '#8b5cf6',
        marginBottom: 4,
        textTransform: 'uppercase',
    },
    legCardDescription: {
        fontSize: 14,
        fontWeight: '600',
        color: 'rgba(255,255,255,0.9)',
    },
    legCardDetails: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.6)',
        marginTop: 4,
    },
    removeButton: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: 'rgba(255,100,100,0.2)',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 12,
    },
    removeButtonText: {
        fontSize: 20,
        color: '#ff9f9f',
        fontWeight: '600',
        marginTop: -2,
    },
    queryPreview: {
        padding: 16,
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: 10,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.1)',
    },
    queryPreviewLabel: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 11,
        fontWeight: '700',
        marginBottom: 8,
        letterSpacing: 1,
    },
    queryPreviewText: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 15,
        fontWeight: '500',
        fontStyle: 'italic',
    },
    errorContainer: {
        padding: 12,
        backgroundColor: 'rgba(255,100,100,0.1)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255,100,100,0.3)',
    },
    errorText: {
        color: '#ff9f9f',
        fontSize: 14,
        fontWeight: '500',
    },
    actionButtons: {
        flexDirection: isMobile ? 'column' : 'row',
        gap: 12,
    },
    actionButton: {
        flex: isMobile ? undefined : 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 14,
        paddingHorizontal: 20,
        borderRadius: 10,
        borderWidth: 1.5,
    },
    addButton: {
        backgroundColor: 'rgba(139, 92, 246, 0.2)',
        borderColor: '#8b5cf6',
    },
    clearButton: {
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderColor: 'rgba(255,255,255,0.3)',
    },
    searchButton: {
        backgroundColor: 'rgba(34, 197, 94, 0.2)',
        borderColor: '#22c55e',
    },
    actionButtonText: {
        color: '#fff',
        fontSize: 15,
        fontWeight: '600',
    },
    actionButtonDisabled: {
        opacity: 0.4,
    },
    comingSoonOverlay: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.7)',
    },
    comingSoonBlur: {
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    comingSoonModal: {
        width: isMobile ? '90%' : 480,
        maxWidth: 480,
        borderRadius: isMobile ? 16 : 24,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: 'rgba(162, 89, 255, 0.3)',
    },
    comingSoonGradient: {
        padding: isMobile ? 24 : 40,
        alignItems: 'center',
    },
    comingSoonTitle: {
        fontSize: isMobile ? 24 : 32,
        fontFamily: 'ClimateCrisis',
        color: '#EBDEFF',
        marginBottom: isMobile ? 12 : 16,
        textTransform: 'uppercase',
    },
    comingSoonText: {
        fontSize: isMobile ? 14 : 16,
        fontFamily: 'Slate',
        color: 'rgba(255,255,255,0.8)',
        textAlign: 'center',
        lineHeight: isMobile ? 20 : 24,
        marginBottom: isMobile ? 24 : 32,
    },
    comingSoonButton: {
        minWidth: 200,
    },
    // Sportsbooks Multi-Select Dropdown Styles
    sportsbooksSelector: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#1a1a1a',
        borderRadius: 8,
        padding: 14,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.3)',
        marginTop: 8,
    },
    sportsbooksSelectorText: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 14,
        flex: 1,
    },
    sportsbooksArrow: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 12,
        marginLeft: 8,
    },
    sportsbooksDropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: '#0d0d0d',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.3)',
        marginTop: 4,
        zIndex: 99999,
        maxHeight: 400,
        overflow: 'hidden',
        ...(Platform.OS === 'web' && {
            boxShadow: '0 12px 48px rgba(0,0,0,0.9)',
        }),
    },
    sportsbooksDropdownHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.15)',
        backgroundColor: '#1a1a1a',
    },
    sportsbooksQuickAction: {
        paddingVertical: 6,
        paddingHorizontal: 10,
        backgroundColor: 'rgba(139,92,246,0.2)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(139,92,246,0.4)',
    },
    sportsbooksQuickActionText: {
        color: '#a78bfa',
        fontSize: 12,
        fontWeight: '600',
    },
    sportsbooksCloseBtn: {
        marginLeft: 'auto',
        paddingVertical: 6,
        paddingHorizontal: 10,
        backgroundColor: 'rgba(74,222,128,0.2)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(74,222,128,0.4)',
    },
    sportsbooksCloseBtnText: {
        color: '#4ade80',
        fontSize: 12,
        fontWeight: '600',
    },
    sportsbooksList: {
        padding: 12,
        maxHeight: 320,
        backgroundColor: '#0d0d0d',
    },
    sportsbooksTierLabel: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 11,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginTop: 12,
        marginBottom: 8,
    },
    sportsbooksGridDropdown: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        backgroundColor: '#0d0d0d',
    },
    // Selected Sportsbooks Summary
    selectedSportsbooksSummary: {
        marginTop: 8,
        padding: 12,
        backgroundColor: '#1e1433',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#4c2889',
    },
    selectedSportsbooksHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    selectedSportsbooksLabel: {
        color: '#c4b5fd',
        fontSize: 13,
        fontWeight: '600',
    },
    clearSportsbooksBtn: {
        color: '#f87171',
        fontSize: 12,
        fontWeight: '600',
    },
    selectedSportsbooksChips: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
    },
    selectedSportsbookChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 4,
        paddingLeft: 8,
        paddingRight: 6,
        backgroundColor: '#4c2889',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: '#7c3aed',
    },
    selectedSportsbookChipText: {
        color: '#e9d5ff',
        fontSize: 11,
        fontWeight: '600',
    },
    selectedSportsbookRemove: {
        color: '#c4b5fd',
        fontSize: 14,
        fontWeight: '600',
    },
});

export default SameGameParlayScreen;
